import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './../ui/dialog';
import { Button } from './../ui/button';
import { Input } from './../ui/input';
import { Label } from './../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './../ui/select';
import { Switch } from './../ui/switch';
import { Save, Plus } from 'lucide-react';

const avatarColors = [
  { value: 'blue', label: 'Azul', gradient: 'from-blue-500 to-cyan-500' },
  { value: 'purple', label: 'Roxo', gradient: 'from-purple-500 to-pink-500' },
  { value: 'green', label: 'Verde', gradient: 'from-emerald-500 to-teal-500' },
  { value: 'orange', label: 'Laranja', gradient: 'from-orange-500 to-amber-500' },
  { value: 'red', label: 'Vermelho', gradient: 'from-red-500 to-rose-500' },
  { value: 'indigo', label: 'Índigo', gradient: 'from-indigo-500 to-blue-500' }
];

export default function MemberModal({ member, open, onClose, onSave }) {
  const [formData, setFormData] = useState(
    member || {
      name: '',
      profession: '',
      email: '',
      avatar_color: 'blue',
      active: true
    }
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) {
      setFormData(member || {
        name: '',
        profession: '',
        email: '',
        avatar_color: 'blue',
        active: true
      });
    }
  }, [open, member]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.profession.trim()) return;
    
    setIsSubmitting(true);
    await onSave(formData);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-white to-slate-50">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            {member ? '✏️ Editar Membro' : '✨ Novo Membro'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-4">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-sm font-medium text-slate-700">
              Nome *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Digite o nome do membro"
              className="mt-1.5"
              autoFocus
            />
          </div>

          {/* Profession */}
          <div>
            <Label htmlFor="profession" className="text-sm font-medium text-slate-700">
              Profissão *
            </Label>
            <Input
              id="profession"
              value={formData.profession}
              onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
              placeholder="Ex: Desenvolvedor, Designer, Gerente..."
              className="mt-1.5"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-slate-700">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@exemplo.com"
              className="mt-1.5"
            />
          </div>

          {/* Avatar Color */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-3 block">
              Cor do Avatar
            </Label>
            <div className="grid grid-cols-6 gap-2">
              {avatarColors.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, avatar_color: color.value })}
                  className={`
                    w-10 h-10 rounded-xl bg-gradient-to-br ${color.gradient}
                    transition-all duration-200 hover:scale-110
                    ${formData.avatar_color === color.value ? 'ring-4 ring-offset-2 ring-blue-400 scale-110' : ''}
                  `}
                  title={color.label}
                />
              ))}
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label htmlFor="active" className="text-sm font-medium text-slate-700">
                Membro Ativo
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                Membros inativos não aparecem na seleção de tarefas
              </p>
            </div>
            <Switch
              id="active"
              checked={formData.active}
              onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
            />
          </div>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="hover:scale-105 transition-all">
            Cancelar
          </Button>
          <Button 
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-lg shadow-blue-200 hover:shadow-xl hover:scale-105 transition-all font-semibold"
            onClick={handleSubmit}
            disabled={!formData.name.trim() || !formData.profession.trim() || isSubmitting}
          >
            {member ? <Save className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {member ? 'Salvar' : 'Criar Membro'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}